import { useEffect, useRef, useState } from "react";
import { Download, FileText, FileUp, Loader2, PlayCircle, Plus, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, downloadFile, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { BulkUpload } from "@/components/BulkUpload";

const InlineEdit = ({ value, onSave, testId, className = "" }) => {
  const [v, setV] = useState(null);
  if (v === null) return <button onClick={() => setV(value)} title="Click to edit" data-testid={testId} className={`text-left truncate hover:text-pinkb hover:underline decoration-dotted ${className}`}>{value}</button>;
  const save = () => { const t = v.trim(); if (t && t !== value) onSave(t); setV(null); };
  return <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setV(null); }} data-testid={`${testId}-input`} className={`rounded border border-input bg-white px-1.5 py-0.5 ${className}`} />;
};

const BLANK = { title: "", category: "", type: "template", description: "", content: "", video_url: "", pack: "", audience: "shared", early_access_until: "" };
export const AUDS = [["shared", "Both audiences"], ["cpg", "CPG only"], ["hospitality", "Hospitality only"]];
const PACKS = [["", "No pack"], ["launch", "Launch campaign"], ["christmas", "Christmas planning"], ["nye", "New Year's Eve"], ["mothers_day", "Mother's Day"], ["easter", "Easter"], ["fathers_day", "Father's Day"], ["valentines", "Valentine's Day"], ["summer_terrace", "Summer terrace season"], ["back_to_school", "Back to school"]];

const NewResource = ({ onClose, onSaved }) => {
  const [f, setF] = useState(BLANK);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.title.trim()) return toast.error("Add a title");
    const fd = new FormData();
    Object.entries({ ...f, category: f.category || "General" }).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/resources", fd);
      toast.success("Added to the library");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="resource-editor">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Add a template or video</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={f.title} onChange={set("title")} placeholder="Title" data-testid="resource-title-input" className="bg-white sm:col-span-2" />
          <Input value={f.category} onChange={set("category")} placeholder="Category e.g. Social Media" data-testid="resource-category-input" className="bg-white" />
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
            <SelectTrigger className="bg-white" data-testid="resource-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="template" data-testid="resource-type-template">Template</SelectItem>
              <SelectItem value="video" data-testid="resource-type-video">Video</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input value={f.description} onChange={set("description")} placeholder="Short description" data-testid="resource-description-input" className="bg-white" />
        <select value={f.pack} onChange={set("pack")} data-testid="resource-pack-select" className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          {PACKS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <select value={f.audience} onChange={set("audience")} data-testid="resource-audience-select" className="h-10 rounded-md border border-input bg-white px-3 text-sm">{AUDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </div>
        {f.type === "video" && <Input value={f.video_url} onChange={set("video_url")} placeholder="YouTube, Loom or Vimeo link" data-testid="resource-video-url-input" className="bg-white" />}
        <Textarea rows={8} value={f.content} onChange={set("content")} placeholder="Template text (markdown supported). Members can copy it." data-testid="resource-content-input" className="bg-white" />
        <button type="button" onClick={() => ref.current.click()} data-testid="resource-file-btn"
          className="h-10 rounded-md border border-dashed border-border hover:border-pinkb text-sm text-muted-foreground flex items-center justify-center gap-2">
          <UploadCloud className="h-4 w-4" />{file ? file.name : "Attach downloadable file (optional)"}
        </button>
        <input ref={ref} type="file" hidden data-testid="resource-file-input" onChange={(e) => setFile(e.target.files[0] || null)} />
        <Button onClick={save} disabled={busy} data-testid="resource-save-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white w-fit">Add to library</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminResources() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const importRef = useRef();
  const [uploadAud, setUploadAud] = useState("shared");
  const importMd = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("audience", uploadAud);
    setImporting(true);
    try {
      const { data } = await api.post("/admin/resources/import", fd);
      toast.success(`Imported ${data.imported} templates from ${data.doc_name}`);
      api.get("/resources").then((r) => setItems(r.data));
    } catch (e) { toast.error(errMsg(e)); } finally { setImporting(false); importRef.current.value = ""; }
  };
  useEffect(() => { api.get("/resources").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const update = async (it, patch) => {
    try {
      await api.put(`/admin/resources/${it.id}`, patch);
      setItems((l) => l.map((x) => (x.id === it.id ? { ...x, ...patch } : x)));
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setAudFor = async (it, audience) => {
    await api.put(`/admin/resources/${it.id}`, { audience });
    setItems((l) => l.map((x) => (x.id === it.id ? { ...x, audience } : x)));
    toast.success("Audience updated");
  };
  const setPackFor = async (it, pack) => {
    await api.put(`/admin/resources/${it.id}`, { pack });
    setItems((l) => l.map((x) => (x.id === it.id ? { ...x, pack } : x)));
    toast.success("Pack updated");
  };
  const remove = async (id) => {
    if (!window.confirm("Remove from the library?")) return;
    await api.delete(`/admin/resources/${id}`);
    setItems((l) => l.filter((x) => x.id !== id));
  };

  return (
    <div>
      <PageHeader eyebrow={`Library · ${items.length} items`} title="Templates & videos.">
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => importRef.current.click()} disabled={importing} variant="outline" data-testid="resource-import-btn" className="rounded-full">
            {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />}Import templates from .md
          </Button>
          <input ref={importRef} type="file" accept=".md,.txt,.markdown" hidden data-testid="resource-import-input" onChange={(e) => importMd(e.target.files[0])} />
          <Button onClick={() => downloadFile("/admin/resources/template-plan.md", "template-plan.md").catch((e) => toast.error(errMsg(e)))} variant="outline" data-testid="template-plan-btn" className="rounded-full"><Download className="h-4 w-4 mr-2" />Template plan for Claude</Button>
          <Button onClick={() => setAdding(true)} data-testid="resource-new-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white"><Plus className="h-4 w-4 mr-2" />Add resource</Button>
        </div>
      </PageHeader>
      <div className="rounded-2xl border border-border bg-card p-5 mb-6 text-sm text-muted-foreground max-w-3xl" data-testid="template-import-help">
        <span className="text-foreground">Writing templates in Claude?</span> Ask Claude to put them in one .md file, with <span className="font-mono">## Category</span> above each group and <span className="font-mono">### Template name</span> above each template. The first line under each template becomes its description. Re-importing a file with the same name replaces those templates. For PDFs, Canva or Word files, use <em>Bulk upload</em> below or <em>Add resource</em>. Click any title or category in the list to edit it.
      </div>
      <label className="mb-4 flex items-center gap-2 text-sm">Audience for imports and bulk uploads
        <select value={uploadAud} onChange={(e) => setUploadAud(e.target.value)} data-testid="upload-audience-select" className="h-9 rounded-md border border-input bg-white px-2 text-sm">{AUDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <span className="text-xs text-muted-foreground">(sections with CPG or Hospitality in the name are tagged automatically)</span></label>
      <BulkUpload audience={uploadAud} onDone={() => api.get("/resources").then((r) => setItems(r.data))} />
      <div className="rounded-2xl border border-border bg-card divide-y divide-border">
        {items.map((it) => (
          <div key={it.id} className="flex flex-wrap items-center gap-4 p-5" data-testid={`admin-resource-${it.id}`}>
            {it.type === "video" ? <PlayCircle className="h-5 w-5 text-rose-600 shrink-0" /> : <FileText className="h-5 w-5 text-pinkb shrink-0" />}
            <div className="min-w-0 flex-1">
              <InlineEdit value={it.title} onSave={(v) => update(it, { title: v })} testId={`admin-resource-title-${it.id}`} className="font-medium" />
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <InlineEdit value={it.category} onSave={(v) => update(it, { category: v })} testId={`admin-resource-category-${it.id}`} />
                {it.file_name && <span className="truncate">· {it.file_name}</span>}{it.video_url && " · video link"}
              </div>
            </div>
            <select value={it.audience || "shared"} onChange={(e) => setAudFor(it, e.target.value)} data-testid={`admin-resource-audience-${it.id}`} className="h-8 rounded-md border border-input bg-white px-2 text-xs">{AUDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <select value={it.pack || ""} onChange={(e) => setPackFor(it, e.target.value)} data-testid={`admin-resource-pack-${it.id}`} className="h-8 rounded-md border border-input bg-white px-2 text-xs">
              {PACKS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button onClick={() => remove(it.id)} data-testid={`admin-resource-delete-${it.id}`} className="p-2 text-muted-foreground hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {adding && <NewResource onClose={() => setAdding(false)} onSaved={(d) => { setItems((l) => [...l, d]); setAdding(false); }} />}
    </div>
  );
}
